import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, CheckCircle2, Eye, Loader2, Monitor, Rocket, Smartphone } from 'lucide-react';
import {
  checkFormStructure,
  formId,
  publicDefinition,
  type FormAnswers,
  type FormDefIssue,
  type FormDefinition,
  type FormDetail,
  type FormSimulation,
  type FormUploadInfo,
} from '@kaba/shared';
import { api, ApiError } from '../../lib/api';
import { navigate } from '../../lib/router';
import { toast } from '../../lib/toast';
import { useSerialSave } from '../../lib/serialSave';
import { Button, cx, Sheet } from '../ui/ui';
import { FormRenderer, Summary } from './FormRenderer';
import { AccessTab } from './editor/AccessTab';
import { ContentTab } from './editor/ContentTab';
import { MappingTab } from './editor/MappingTab';
import { RulesTab } from './editor/RulesTab';
import { evaluateForm } from '@kaba/shared';
import { CommentHtml } from '../card/CommentEditor';

type Tab = 'inhalt' | 'regeln' | 'karte' | 'zugang';
/** Bereich, Beschriftung, kurze Beschriftung am Handy */
const TABS: [Tab, string, string][] = [
  ['inhalt', 'Inhalt', 'Inhalt'],
  ['regeln', 'Regeln', 'Regeln'],
  ['karte', 'Kartenanlage', 'Karte'],
  ['zugang', 'Zugang & Abschluss', 'Zugang'],
];

export const STATUS_TEXT: Record<string, string> = { draft: 'Entwurf', active: 'Aktiv', paused: 'Pausiert', archived: 'Archiviert' };

/** Formulareditor: Entwurf bearbeiten, Vorschau mit Ergebnisprüfung, veröffentlichen (U01–U03, V01) */
export function FormEditorPage({ boardId, formId: id }: { boardId: string; formId: string }) {
  const { data: detail, refetch, error } = useQuery({ queryKey: ['form', id], queryFn: () => api.get<FormDetail>(`/api/forms/${id}`) });
  const [def, setDef] = useState<FormDefinition | null>(null);
  const [tab, setTab] = useState<Tab>('inhalt');
  const [selected, setSelected] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);
  const [publish, setPublish] = useState(false);
  const [version, setVersion] = useState<{ number: number; definition: FormDefinition } | null>(null);
  const conflictRev = useRef<number | null>(null);
  // Höchstens ein Speichervorgang gleichzeitig; neuere Änderungen werden nachgespeichert (F01)
  const { saver, state: saveState } = useSerialSave<FormDefinition, { revision: number }>({
    delayMs: 700,
    send: async (d, revision) => {
      const r = await api.put<{ revision: number }>(`/api/forms/${id}/draft`, { definition: d, revision });
      void refetch();
      return r;
    },
    classify: (e) => {
      if (e instanceof ApiError && e.status === 409) {
        conflictRev.current = (e.data as { details?: { revision?: number } } | undefined)?.details?.revision ?? null;
        return conflictRev.current === null ? 'error' : 'conflict';
      }
      if (e instanceof ApiError && e.status >= 400 && e.status < 500) {
        toast.error((e as Error).message);
        return 'error';
      }
      return 'offline';
    },
  });

  useEffect(() => {
    if (!detail) return;
    // Serverstand nur übernehmen, wenn nichts offen ist und er neuer ist (Revision steigt nur)
    if (!def || (!saver.dirty && detail.draftRevision > saver.revision)) {
      setDef(detail.draft);
      saver.reset(detail.draft, detail.draftRevision);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail]);
  useEffect(() => {
    document.title = `${detail?.title ?? 'Formular'} – KaBa`;
  }, [detail?.title]);

  const flush = useCallback(() => saver.flush(), [saver]);
  const change = (d: FormDefinition) => {
    setDef(d);
    saver.change(d);
  };
  const failed = saveState.kind === 'failed' ? saveState.reason : null;
  const saveText =
    saveState.kind === 'saving'
      ? 'Speichere …'
      : failed === 'conflict'
        ? 'An anderer Stelle geändert – deine Änderungen sind noch nicht gespeichert'
        : failed === 'offline'
          ? 'Nicht gespeichert – keine Verbindung, wir versuchen es weiter'
          : failed
            ? 'Nicht gespeichert'
            : saver.dirty
              ? 'Ungespeichert'
              : 'Entwurf gespeichert';

  if (error) return <p className="p-6 text-sm">{(error as Error).message}</p>;
  if (!detail || !def) return <p className="p-6 text-sm text-[var(--ink-soft)]">Lade …</p>;
  const structure = checkFormStructure(def);
  const blocking = structure.filter((i) => i.blocking).length;

  return (
    <div className="flex min-h-full flex-col bg-[var(--surface)]">
      <header className="pt-safe sticky top-0 z-20 border-b border-[var(--line)] bg-[var(--panel)]">
        <div className="flex flex-wrap items-center gap-2 px-3 py-2 sm:px-4">
          <button type="button" className="inline-flex size-10 items-center justify-center rounded-md hover:bg-black/5 dark:hover:bg-white/10" aria-label="Zurück zum Board" onClick={() => navigate(`/b/${boardId}?formulare=1`)}>
            <ArrowLeft size={20} />
          </button>
          <div className="min-w-0 flex-1 basis-0">
            <input
              className="w-full truncate rounded-md bg-transparent px-1 text-base font-semibold outline-none focus:bg-black/5 dark:focus:bg-white/10"
              value={def.title}
              maxLength={200}
              aria-label="Titel des Formulars"
              onChange={(e) => change({ ...def, title: e.target.value })}
            />
            <p className="flex flex-wrap items-center gap-x-2 px-1 text-xs text-[var(--ink-soft)]">
              <span className={cx('rounded px-1', detail.status === 'active' ? 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900 dark:text-emerald-100' : 'bg-black/5 dark:bg-white/10')}>
                {STATUS_TEXT[detail.status]}
                {detail.activeVersion ? ` · Version ${detail.activeVersion}` : ''}
              </span>
              {detail.repairIssues.length > 0 && <span className="rounded bg-rose-100 px-1 text-rose-900 dark:bg-rose-900 dark:text-rose-100">Reparatur nötig</span>}
              {detail.activeVersion && detail.hasDraftChanges && <span>Änderungen noch nicht veröffentlicht</span>}
              <span aria-live="polite">{saveText}</span>
              {failed === 'conflict' && (
                <>
                  <button className="min-h-10 underline" onClick={() => void saver.overwrite(conflictRev.current ?? saver.revision)}>
                    Meine Fassung speichern
                  </button>
                  <button
                    className="min-h-10 underline"
                    onClick={() => {
                      if (!window.confirm('Die andere Fassung laden? Deine ungespeicherten Änderungen gehen dabei verloren.')) return;
                      void refetch().then((r) => {
                        if (!r.data) return;
                        setDef(r.data.draft);
                        saver.reset(r.data.draft, r.data.draftRevision);
                      });
                    }}
                  >
                    Andere Fassung laden …
                  </button>
                </>
              )}
            </p>
          </div>
          <Button onClick={() => setPreview(true)} className="max-sm:size-11 max-sm:px-0" aria-label="Vorschau">
            <Eye size={16} aria-hidden /> <span className="max-sm:hidden">Vorschau</span>
          </Button>
          <Button
            variant="primary"
            onClick={async () => {
              await flush();
              setPublish(true);
            }}
          >
            <Rocket size={16} className="max-sm:hidden" aria-hidden /> Veröffentlichen
          </Button>
        </div>
        {detail.repairIssues.length > 0 && (
          <div role="alert" className="mx-3 mb-2 rounded-lg bg-rose-50 p-2.5 text-sm text-rose-900 sm:mx-4 dark:bg-rose-950 dark:text-rose-100">
            <strong>Das Formular nimmt keine Einsendungen an:</strong> {detail.repairIssues.join(' ')} Passe die Kartenanlage an und veröffentliche neu.
          </div>
        )}
        <div role="tablist" aria-label="Bereiche des Editors" className="flex gap-1 overflow-x-auto px-3 sm:px-4">
          {TABS.map(([t, l, short]) => (
            <button
              key={t}
              role="tab"
              aria-selected={tab === t}
              onClick={() => setTab(t)}
              className={cx('min-h-11 flex-1 shrink-0 border-b-2 px-2 text-sm font-medium sm:flex-none sm:px-3', tab === t ? 'border-accent text-[var(--ink)]' : 'border-transparent text-[var(--ink-soft)] hover:text-[var(--ink)]')}
            >
              <span className="sm:hidden" aria-hidden>
                {short}
              </span>
              <span className="max-sm:sr-only">{l}</span>
              {t === 'regeln' && blocking > 0 && <span className="ml-1 rounded-full bg-rose-600 px-1.5 text-[10px] text-white">{blocking}</span>}
            </button>
          ))}
        </div>
      </header>

      <main className="flex-1 p-3 sm:p-4">
        <div role="tabpanel" aria-label={TABS.find(([t]) => t === tab)![1]}>
        {tab === 'inhalt' && (
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-sm font-medium">
              Einleitung
              <textarea
                className="min-h-16 rounded-lg border border-[var(--line)] bg-[var(--panel)] p-2 text-base font-normal sm:text-sm"
                maxLength={4000}
                value={def.intro}
                placeholder="Kurzer Einführungstext über dem Formular"
                onChange={(e) => change({ ...def, intro: e.target.value })}
              />
            </label>
            <ContentTab def={def} onChange={change} selected={selected} onSelect={setSelected} />
          </div>
        )}
        {tab === 'regeln' && (
          <RulesTab
            def={def}
            onSelect={(x) => {
              setSelected(x.startsWith('p') && def.pages.some((p) => p.id === x) ? null : x);
              setTab('inhalt');
            }}
          />
        )}
        {tab === 'karte' && <MappingTab def={def} detail={detail} onChange={change} />}
        {tab === 'zugang' && (
          <AccessTab
            def={def}
            detail={detail}
            onChange={change}
            onReload={() => void refetch()}
            onViewVersion={async (vid) => setVersion(await api.get(`/api/forms/${id}/versions/${vid}`))}
          />
        )}
        </div>
      </main>

      <PreviewSheet open={preview} onClose={() => setPreview(false)} def={def} formIdValue={id} onSaveTest={(name, answers) => change({ ...def, tests: [...def.tests, { id: formId('t'), name, answers }] })} />
      <PublishDialog open={publish} onClose={() => setPublish(false)} detail={detail} revision={saver.revision} onDone={() => void refetch()} onOpenPreview={() => (setPublish(false), setPreview(true))} />
      <Sheet open={!!version} onOpenChange={(o) => !o && setVersion(null)} title={version ? `Version ${version.number}` : 'Version'} side="right" widthClass="sm:w-[640px]">
        {version && (
          <div className="p-4">
            <Summary def={publicDefinition(version.definition)} ev={evaluateForm(version.definition, {})} uploads={[]} />
            <p className="mt-3 text-xs text-[var(--ink-soft)]">Die Übersicht zeigt die Fragen ohne Antworten. Bedingte Fragen erscheinen nur, wenn ihre Bedingung erfüllt ist.</p>
          </div>
        )}
      </Sheet>
    </div>
  );
}

/** Vorschau mit realer Logik, ohne Kartenanlage, Nachrichten oder echte Uploads (U03) */
function PreviewSheet({ open, onClose, def, formIdValue, onSaveTest }: { open: boolean; onClose: () => void; def: FormDefinition; formIdValue: string; onSaveTest: (name: string, a: FormAnswers) => void }) {
  const [answers, setAnswers] = useState<FormAnswers>({});
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [sim, setSim] = useState<FormSimulation | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploads, setUploads] = useState<FormUploadInfo[]>([]);
  const [key, setKey] = useState(0);
  useEffect(() => {
    if (open) setSim(null);
  }, [open]);
  const check = async () => {
    setBusy(true);
    try {
      setSim(await api.post<FormSimulation>(`/api/forms/${formIdValue}/simulate`, { definition: def, answers }));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title="Vorschau" side="wide" bare>
      <div className="pt-safe flex flex-wrap items-center gap-2 border-b border-[var(--line)] px-3 py-2">
        <h2 className="flex-1 text-base font-semibold">Vorschau</h2>
        <div role="group" aria-label="Gerät" className="hidden rounded-lg bg-black/[0.04] p-0.5 sm:inline-flex dark:bg-white/5">
          {(['desktop', 'mobile'] as const).map((d) => (
            <button key={d} type="button" aria-pressed={device === d} onClick={() => setDevice(d)} className={cx('inline-flex min-h-9 items-center gap-1.5 rounded-md px-3 text-sm', device === d ? 'bg-[var(--panel)] font-medium shadow-sm' : 'text-[var(--ink-soft)]')}>
              {d === 'desktop' ? <Monitor size={15} /> : <Smartphone size={15} />} {d === 'desktop' ? 'Desktop' : 'Handy'}
            </button>
          ))}
        </div>
        <Button
          size="sm"
          onClick={() => {
            setAnswers({});
            setUploads([]);
            setSim(null);
            setKey((k) => k + 1);
          }}
        >
          Zurücksetzen
        </Button>
        <Button size="sm" variant="primary" onClick={() => void check()} disabled={busy}>
          {busy && <Loader2 size={14} className="animate-spin" />} Ergebnis prüfen
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Schließen
        </Button>
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-1 overflow-y-auto lg:grid-cols-[minmax(0,1fr)_24rem] lg:overflow-hidden">
        <div className="flex justify-center bg-[var(--surface)] p-3 sm:p-6 lg:overflow-y-auto">
          <div className={cx('h-fit w-full rounded-2xl bg-[var(--panel)] p-4 shadow-sm sm:p-6', device === 'mobile' ? 'max-w-[390px]' : 'max-w-2xl')}>
            <h1 className="mb-1 text-xl font-semibold">{def.title}</h1>
            {def.intro && <p className="mb-4 text-sm whitespace-pre-line">{def.intro}</p>}
            <FormRenderer
              key={key}
              def={publicDefinition(def)}
              answers={answers}
              onChange={(a) => {
                setAnswers(a);
                setSim(null);
              }}
              uploads={{
                list: uploads,
                // Vorschau: keine produktiven Uploads, nur Platzhalter (U03)
                upload: async (k, file) => {
                  const item: FormUploadInfo = { id: `vorschau-${formId('f')}`, key: k, name: file.name, mime: file.type || 'application/octet-stream', size: file.size, status: 'ready' };
                  setUploads((u) => [...u, item]);
                  return item;
                },
                remove: async (rid) => setUploads((u) => u.filter((x) => x.id !== rid)),
                url: () => null,
              }}
              preview
              submitLabel="Ergebnis prüfen"
              onSubmit={check}
            />
          </div>
        </div>
        <aside aria-label="Ergebnis" className="flex flex-col gap-3 border-t border-[var(--line)] p-4 lg:overflow-y-auto lg:border-t-0 lg:border-l">
          <h3 className="text-sm font-semibold">Ergebnis prüfen</h3>
          {!sim && <p className="text-sm text-[var(--ink-soft)]">Fülle das Formular aus und wähle „Ergebnis prüfen“. Es wird keine Karte angelegt und nichts verschickt.</p>}
          {sim && (
            <>
              {sim.card.errors.length > 0 && (
                <ul className="rounded-lg bg-rose-50 p-2.5 text-sm text-rose-900 dark:bg-rose-950 dark:text-rose-100">
                  {sim.card.errors.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              )}
              {sim.issues.filter((i) => !/Datei/.test(i.message)).length > 0 && (
                <p className="rounded-lg bg-amber-50 p-2.5 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-100">
                  {sim.issues.length === 1 ? 'Eine Angabe fehlt oder ist ungültig' : `${sim.issues.length} Angaben fehlen oder sind ungültig`} – so würde das Formular nicht angenommen.
                </p>
              )}
              <dl className="flex flex-col gap-2 text-sm">
                <Out label="Spalte" value={sim.card.column ?? '–'} origin={sim.card.origins.column} />
                <Out label="Regel" value={sim.card.route ?? 'Keine – Standardfall'} />
                <Out label="Titel" value={sim.card.title || '–'} origin={sim.card.origins.title} />
                <Out label="Labels" value={sim.card.labels.join(', ') || '–'} origin={sim.card.origins.labels} />
                <Out label="Priorität" value={['Keine', 'Niedrig', 'Mittel', 'Hoch', 'Dringend'][sim.card.priority]!} origin={sim.card.origins.priority} />
                <Out label="Zuständig" value={sim.card.assignees.join(', ') || '–'} origin={sim.card.origins.assignees} />
                {sim.card.start && <Out label="Beginn" value={sim.card.start} origin={sim.card.origins.start} />}
                <Out label="Fällig" value={sim.card.due ?? '–'} origin={sim.card.origins.due} />
                {sim.card.fields.map((f) => (
                  <Out key={f.name} label={f.name} value={f.value} />
                ))}
              </dl>
              {sim.card.hints.map((h) => (
                <p key={h} className="text-xs text-amber-800 dark:text-amber-200">
                  {h}
                </p>
              ))}
              {sim.card.description && (
                <details className="text-sm">
                  <summary className="cursor-pointer font-medium">Beschreibung</summary>
                  <div className="mt-1 rounded-lg bg-[var(--column)] p-2 text-sm [&_p]:mb-1 [&_ul]:list-disc [&_ul]:pl-5">
                    <CommentHtml html={sim.card.description} members={[]} />
                  </div>
                </details>
              )}
              <Button
                size="sm"
                onClick={() => {
                  const name = window.prompt('Name des Testfalls', sim.card.route ? `Weg „${sim.card.route}“` : 'Standardfall');
                  if (!name) return;
                  // Dateiantworten in Testfällen weglassen (keine echten Uploads)
                  const clean: FormAnswers = {};
                  for (const [k, v] of Object.entries(answers)) if (!(Array.isArray(v) && v.some((x) => typeof x === 'string' && x.startsWith('vorschau-')))) clean[k] = v;
                  onSaveTest(name.slice(0, 80), clean);
                  toast.info('Testfall gespeichert. Er wird vor jeder Veröffentlichung geprüft.');
                }}
                disabled={sim.card.errors.length > 0}
              >
                Als Testfall speichern
              </Button>
            </>
          )}
        </aside>
      </div>
    </Sheet>
  );
}

function Out({ label, value, origin }: { label: string; value: string; origin?: string }) {
  return (
    <div className="grid grid-cols-[6.5rem_1fr] gap-2">
      <dt className="text-[var(--ink-soft)]">{label}</dt>
      <dd className="[overflow-wrap:anywhere]">
        {value}
        {origin && <span className="block text-xs text-[var(--ink-soft)]">aus: {origin}</span>}
      </dd>
    </div>
  );
}

/** Veröffentlichen: Prüfung, was sichtbar wird, dann atomar umschalten (A02, V01) */
function PublishDialog({ open, onClose, detail, revision, onDone, onOpenPreview }: { open: boolean; onClose: () => void; detail: FormDetail; revision: number; onDone: () => void; onOpenPreview: () => void }) {
  const [issues, setIssues] = useState<FormDefIssue[] | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setIssues(null);
    api
      .post<{ issues: FormDefIssue[] }>(`/api/forms/${detail.id}/check`)
      .then((r) => setIssues(r.issues))
      .catch((e) => toast.error((e as Error).message));
  }, [open, detail.id]);
  const blocking = issues?.filter((i) => i.blocking) ?? [];
  const hints = issues?.filter((i) => !i.blocking) ?? [];
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title="Veröffentlichen" side="center">
      <div className="flex flex-col gap-3 p-4 text-sm">
        {!issues && <p className="text-[var(--ink-soft)]">Prüfe das Formular …</p>}
        {blocking.length > 0 && (
          <>
            <p className="font-semibold">Vorher noch erledigen:</p>
            <ul className="flex flex-col gap-1.5">
              {blocking.map((i, k) => (
                <li key={k} className="flex gap-2">
                  <AlertTriangle size={15} className="mt-0.5 shrink-0 text-rose-600" aria-hidden /> {i.message}
                </li>
              ))}
            </ul>
            {blocking.some((i) => i.where === 'tests') && (
              <Button onClick={onOpenPreview} className="self-start">
                Vorschau öffnen
              </Button>
            )}
          </>
        )}
        {issues && !blocking.length && (
          <>
            <p className="flex items-center gap-2 font-semibold text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 size={16} aria-hidden /> Alles bereit.
            </p>
            <div className="rounded-lg bg-[var(--column)] p-3">
              <p className="mb-1 font-medium">Für Ausfüllende sichtbar</p>
              <p>Titel, Einleitung, Fragen, Hilfetexte, Antwortmöglichkeiten und die Bedingungen, wann eine Frage erscheint.</p>
              <p className="mt-2 mb-1 font-medium">Nicht sichtbar</p>
              <p>Board, Zielspalten, Labels, Zuständige, Zuordnungsregeln und Testfälle.</p>
            </div>
            {hints.map((h, k) => (
              <p key={k} className="text-xs text-[var(--ink-soft)]">
                Hinweis: {h.message}
              </p>
            ))}
            {detail.activeVersion && <p>Begonnene Einsendungen der bisherigen Version können noch abgeschlossen werden. Der Link bleibt gleich.</p>}
          </>
        )}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Abbrechen</Button>
          <Button
            variant="primary"
            disabled={!issues || blocking.length > 0 || busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.post(`/api/forms/${detail.id}/publish`, { revision });
                toast.info('Veröffentlicht.');
                onDone();
                onClose();
              } catch (e) {
                const d = e instanceof ApiError ? (e.data as { details?: { issues?: FormDefIssue[] } }) : undefined;
                if (d?.details?.issues) setIssues(d.details.issues);
                else toast.error((e as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            {detail.activeVersion ? `Version ${detail.activeVersion + 1} veröffentlichen` : 'Veröffentlichen'}
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
